import { $, $$, toast } from './lib.js';

const SPEED_KEY = 'wika.playbackRate';
const PLAY_ICON = '<svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24"><path d="M8 5.5v13l10.5-6.5z" fill="currentColor"/></svg>';
const PAUSE_ICON = '<svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24"><path d="M8 5h3v14H8zM13 5h3v14h-3z" fill="currentColor"/></svg>';

/**
 * One audio element for the whole page: per-line play buttons, "play all"
 * queues, and a sticky bar with pause/resume, stop and speed.
 * Audio is fetched lazily on first play; the server caches it.
 */
class Player {
  constructor(bar) {
    this.audio = new Audio();
    this.audio.preload = 'none';
    this.audio.preservesPitch = true;
    this.bar = bar;
    this.queue = [];
    this.current = null;
    this.rate = Number(localStorage.getItem(SPEED_KEY)) || 1;

    this.audio.addEventListener('playing', () => this.render());
    this.audio.addEventListener('pause', () => this.render());
    this.audio.addEventListener('timeupdate', () => this.progress());
    this.audio.addEventListener('ended', () => this.next());
    this.audio.addEventListener('error', () => this.fail());

    if (bar) {
      $('[data-audio-toggle]', bar).addEventListener('click', () => this.toggle());
      $('[data-audio-stop]', bar).addEventListener('click', () => this.stop());
      const speed = $('[data-audio-speed]', bar);
      speed.value = String(this.rate);
      speed.addEventListener('change', () => this.setRate(Number(speed.value)));
    }
  }

  /** @param {HTMLButtonElement[]} buttons */
  play(buttons) {
    if (buttons.length === 1 && this.current === buttons[0] && !this.audio.paused) {
      this.audio.pause();
      return;
    }
    if (buttons.length === 1 && this.current === buttons[0] && this.audio.paused && this.audio.currentSrc) {
      this.audio.play();
      return;
    }
    this.queue = buttons.slice(1);
    this.start(buttons[0]);
  }

  start(button) {
    this.clearCurrent();
    this.current = button;
    button.classList.add('is-loading');
    button.closest('[data-block]')?.classList.add('is-playing');
    this.audio.src = button.dataset.audioSrc;
    this.audio.playbackRate = this.rate;
    this.audio.play().catch(() => {});
    if (this.bar) {
      this.bar.hidden = false;
      const label = button.closest('[data-audio-label]')?.dataset.audioLabel || button.getAttribute('aria-label') || 'Playing';
      $('[data-audio-label]', this.bar).textContent = label;
    }
    button.closest('[data-block]')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    this.render();
  }

  next() {
    const upcoming = this.queue.shift();
    if (upcoming) {
      this.start(upcoming);
    } else {
      this.stop();
    }
  }

  toggle() {
    if (!this.current) return;
    if (this.audio.paused) this.audio.play().catch(() => {});
    else this.audio.pause();
  }

  stop() {
    this.audio.pause();
    this.audio.removeAttribute('src');
    this.audio.load();
    this.queue = [];
    this.clearCurrent();
    this.current = null;
    if (this.bar) this.bar.hidden = true;
  }

  setRate(rate) {
    this.rate = rate;
    this.audio.playbackRate = rate;
    localStorage.setItem(SPEED_KEY, String(rate));
  }

  fail() {
    if (!this.current || !this.audio.getAttribute('src')) return;
    toast('Audio isn’t available for this line right now. You can keep reading.', { type: 'error' });
    const failed = this.current;
    this.clearCurrent();
    this.current = null;
    if (this.queue.length) {
      this.next();
    } else if (this.bar) {
      this.bar.hidden = true;
    }
    failed.focus();
  }

  clearCurrent() {
    if (!this.current) return;
    this.current.classList.remove('is-loading');
    this.current.setAttribute('aria-pressed', 'false');
    this.current.innerHTML = PLAY_ICON;
    this.current.closest('[data-block]')?.classList.remove('is-playing');
  }

  render() {
    const playing = !this.audio.paused;
    if (this.current) {
      this.current.classList.toggle('is-loading', this.audio.readyState < 3 && playing);
      this.current.setAttribute('aria-pressed', String(playing));
      this.current.innerHTML = playing ? PAUSE_ICON : PLAY_ICON;
    }
    if (this.bar) {
      const toggle = $('[data-audio-toggle]', this.bar);
      toggle.setAttribute('aria-label', playing ? 'Pause' : 'Resume');
      $('[data-icon-pause]', toggle).hidden = !playing;
      $('[data-icon-play]', toggle).hidden = playing;
    }
  }

  progress() {
    if (!this.bar || !this.audio.duration) return;
    $('[data-audio-progress]', this.bar).style.width = `${(100 * this.audio.currentTime) / this.audio.duration}%`;
  }
}

export function initAudio() {
  if (!$('[data-audio-src]')) return;
  const player = new Player($('[data-audio-bar]'));

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-audio-src]');
    if (button) {
      event.preventDefault();
      player.play([button]);
      return;
    }
    const all = event.target.closest('[data-play-all]');
    if (all) {
      const section = document.getElementById(all.dataset.playAll);
      player.play($$('[data-audio-src]', section));
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && player.current) player.stop();
  });
}
